<script setup lang="ts">
import { ref, watch } from "vue";
import { onClickOutside, useMediaQuery } from "@vueuse/core";

const props = withDefaults(
  defineProps<{
    modelValue?: "in" | "cm";
    placement?: "auto" | "left" | "right" | "bottom-left" | "bottom-right";
  }>(),
  {
    modelValue: "in",
    placement: "auto",
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: "in" | "cm"): void;
  (e: "applyGlobally", value: boolean): void;
}>();

const isDesktop = useMediaQuery("(min-width: 768px)");

const showPrompt = ref(false);
const containerRef = ref<HTMLElement | null>(null);
const computedPlacement = ref<"left" | "right" | "bottom-left" | "bottom-right">("right");

onClickOutside(containerRef, () => {
  showPrompt.value = false;
});

function calculatePlacement() {
  if (!containerRef.value) {
    computedPlacement.value = isDesktop.value ? (props.placement === "left" ? "left" : "right") : "bottom-left";
    return;
  }
  const rect = containerRef.value.getBoundingClientRect();
  const spaceRight = window.innerWidth - rect.right;
  const spaceLeft = rect.left;

  // On mobile / narrow screens (< 768px), horizontal side placement (left/right) is too wide.
  // Always place below the toggle:
  if (!isDesktop.value) {
    if (rect.right > window.innerWidth / 2 || spaceLeft >= 160) {
      computedPlacement.value = "bottom-left";
    } else {
      computedPlacement.value = "bottom-right";
    }
    return;
  }

  // Desktop (>= 768px):
  if (props.placement === "left") {
    if (spaceLeft >= 270) {
      computedPlacement.value = "left";
    } else {
      computedPlacement.value = "bottom-left";
    }
    return;
  }

  if (props.placement === "right") {
    if (spaceRight >= 270) {
      computedPlacement.value = "right";
    } else {
      computedPlacement.value = "bottom-right";
    }
    return;
  }

  // Auto desktop:
  if (spaceRight >= 270) {
    computedPlacement.value = "right";
  } else if (spaceLeft >= 270) {
    computedPlacement.value = "left";
  } else {
    computedPlacement.value = "bottom-left";
  }
}

watch(showPrompt, (val) => {
  if (val) {
    calculatePlacement();
  }
});

function handleSelect(unit: "in" | "cm") {
  if (props.modelValue === unit) return;
  emit("update:modelValue", unit);

  if (unit === "cm") {
    calculatePlacement();
    showPrompt.value = true;
  } else {
    showPrompt.value = false;
    setGlobalUnit("in");
  }
}

const { setGlobalUnit } = useMeasurementUnit();

function handleScopeChoice(applyGlobally: boolean) {
  showPrompt.value = false;
  if (applyGlobally && props.modelValue) {
    setGlobalUnit(props.modelValue);
  }
  emit("applyGlobally", applyGlobally);
}
</script>

<template>
  <div ref="containerRef" class="relative inline-flex items-center">
    <!-- IN / CM Toggle Buttons -->
    <div class="inline-flex rounded-[6px] border border-on-primary-border bg-button-secondary p-0.5 relative">
      <button
        type="button"
        @click="handleSelect('in')"
        :class="[
          'px-3.5 py-1 text-xs font-semibold rounded-[6px] transition duration-200 cursor-pointer',
          modelValue === 'in'
            ? 'bg-white text-on-primary-heading shadow-sm'
            : 'text-on-primary-body hover:text-on-primary-heading',
        ]"
      >
        IN
      </button>
      <button
        type="button"
        @click="handleSelect('cm')"
        :class="[
          'px-3.5 py-1 text-xs font-semibold rounded-[6px] transition duration-200 cursor-pointer',
          modelValue === 'cm'
            ? 'bg-white text-on-primary-heading shadow-sm'
            : 'text-on-primary-body hover:text-on-primary-heading',
        ]"
      >
        CM
      </button>
    </div>

    <!-- Scope Prompt Popover / Tooltip -->
    <Transition
      enter-active-class="transition duration-200 ease-out"
      :enter-from-class="
        computedPlacement === 'right'
          ? 'opacity-0 -translate-x-3.5'
          : computedPlacement === 'left'
            ? 'opacity-0 translate-x-3.5'
            : 'opacity-0 translate-y-1.5 scale-95'
      "
      :enter-to-class="
        computedPlacement === 'right' || computedPlacement === 'left'
          ? 'opacity-100 translate-x-0'
          : 'opacity-100 translate-y-0 scale-100'
      "
      leave-active-class="transition duration-150 ease-in"
      :leave-from-class="
        computedPlacement === 'right' || computedPlacement === 'left'
          ? 'opacity-100 translate-x-0'
          : 'opacity-100 translate-y-0 scale-100'
      "
      :leave-to-class="
        computedPlacement === 'right'
          ? 'opacity-0 -translate-x-2'
          : computedPlacement === 'left'
            ? 'opacity-0 translate-x-2'
            : 'opacity-0 translate-y-1 scale-95'
      "
    >
      <div
        v-if="showPrompt"
        :class="[
          'absolute z-50',
          computedPlacement === 'right' && 'left-full top-1/2 -translate-y-1/2 ml-3',
          computedPlacement === 'left' && 'right-full top-0 mr-3',
          computedPlacement === 'bottom-left' && 'top-full right-0 mt-2.5',
          computedPlacement === 'bottom-right' && 'top-full left-0 mt-2.5',
        ]"
        role="dialog"
        aria-label="Unit change scope prompt"
      >
        <div class="relative w-64 max-w-[calc(100vw-2rem)] p-3.5 bg-white border border-on-primary-border rounded-lg shadow-lg text-left">
          <!-- Arrow Pointer -->
          <div
            v-if="computedPlacement === 'right'"
            class="absolute -left-[5px] top-1/2 -translate-y-1/2 size-2.5 bg-white border-b border-l border-on-primary-border rotate-45"
          ></div>
          <div
            v-else-if="computedPlacement === 'left'"
            class="absolute -right-[5px] top-3.5 size-2.5 bg-white border-t border-r border-on-primary-border rotate-45"
          ></div>
          <div
            v-else-if="computedPlacement === 'bottom-left'"
            class="absolute -top-[5px] right-[20px] size-2.5 bg-white border-t border-l border-on-primary-border rotate-45"
          ></div>
          <div
            v-else
            class="absolute -top-[5px] left-[20px] size-2.5 bg-white border-t border-l border-on-primary-border rotate-45"
          ></div>

          <p class="text-xs sm:text-[13px] font-medium text-on-primary-heading leading-snug mb-3">
            Do you want to apply this unit change to all pages?
          </p>

          <div class="flex items-center gap-2">
            <button
              type="button"
              @click="handleScopeChoice(true)"
              class="px-3.5 py-1 text-xs font-semibold rounded-[5px] bg-on-primary-heading text-white hover:bg-neutral-800 transition duration-150 cursor-pointer"
            >
              Yes
            </button>
            <button
              type="button"
              @click="handleScopeChoice(false)"
              class="px-3.5 py-1 text-xs font-semibold rounded-[5px] border border-on-primary-border bg-button-secondary text-on-primary-heading hover:bg-neutral-200/70 transition duration-150 cursor-pointer"
            >
              No
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>
